// /me/rate — first-party "Rate your experience" page (Trustpilot-style).
//
// Opened from the post-visit email with an opaque invitation token. The token
// is a bearer credential scoped to one (user, visit): it must never reach
// PostHog/GTM/Bugsnag, so
//   - the server's inline script stashes it in sessionStorage.kickRatingToken
//     and rewrites the URL to /me/rate before any tracker loads;
//   - if React still sees a :token param (script blocked, hard refresh of an
//     old tab…) we store it and scrub the URL ourselves, immediately;
//   - index.jsx skips PostHog entirely on this path.
//
// State machine (plan §2): loading → invalid | expired | error | rate → thanks
// (Edit goes back to rate, pre-filled, with the "updated review" copy). A link
// that already has a rating opens straight in thanks.

import Skeleton from "@mui/material/Skeleton";
import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  RATING_COLORS,
  RATING_THANKS_COLUMN,
  RATING_TILE_PICK,
} from "~/shared/styles/ratingPalette";
import CompanyCard from "./CompanyCard.jsx";
import RatingForm from "./RatingForm.jsx";
import RatingShell from "./RatingShell.jsx";
import RatingThankYou, { RatingThankYouBand } from "./RatingThankYou.jsx";
import { NoticeBox, PillButton, RatingLink } from "./ratingUi.jsx";
import {
  RATING_STATUS_EXPIRED,
  RATING_STATUS_OK,
  canSubmitRating,
} from "./ratingUtils.js";

export const RATING_TOKEN_STORAGE_KEY = "kickRatingToken";
export const RATING_SCRUBBED_PATH = "/me/rate";
export const RATING_PAGE_TITLE = "Rate your experience · Kick";

export const GENERIC_SUBMIT_ERROR = "Something went wrong — please try again.";
export const RATE_LIMITED_SUBMIT_ERROR =
  "Too many attempts — please wait a while and try again.";

// How far the review card straddles the lower band on the thank-you page.
const THANKS_BAND_OVERLAP = 56;

export const PHASE = {
  LOADING: "loading",
  INVALID: "invalid",
  EXPIRED: "expired",
  ERROR: "error",
  RATE: "rate",
  THANKS: "thanks",
};

const EMPTY_FORM = {
  stars: 0,
  comment: "",
  tags: [],
  confirmedGenuine: false, // client-side submit gate, never sent
  website: "", // honeypot — always sent, always empty for humans
};

const JSON_HEADERS = { Accept: "application/json" };

function invitationURL(token) {
  return `/me/rating-invitations/${encodeURIComponent(token)}`;
}

function ratingURL(token) {
  return `${invitationURL(token)}/rating`;
}

function readStoredToken() {
  try {
    return window.sessionStorage.getItem(RATING_TOKEN_STORAGE_KEY) || "";
  } catch (e) {
    return "";
  }
}

function storeToken(token) {
  try {
    window.sessionStorage.setItem(RATING_TOKEN_STORAGE_KEY, token);
  } catch (e) {
    // Storage disabled (private mode, blocked cookies) — the in-memory token
    // still works for this page load.
  }
}

function scrubTokenFromURL() {
  try {
    window.history.replaceState(null, "", RATING_SCRUBBED_PATH);
  } catch (e) {
    // ignore
  }
}

// sessionStorage wins (the server script already scrubbed the URL); a token
// still in the URL is stored and scrubbed at once, before any effect runs.
export function resolveRatingToken(paramToken) {
  const stored = readStoredToken();
  const fromURL = (paramToken || "").trim();
  if (fromURL) {
    scrubTokenFromURL();
  }
  if (stored) {
    return stored;
  }
  if (fromURL) {
    storeToken(fromURL);
    return fromURL;
  }
  return "";
}

function phaseForHTTPStatus(status) {
  if (status === 404) {
    return PHASE.INVALID;
  }
  if (status === 410) {
    return PHASE.EXPIRED;
  }
  return null;
}

function formFromRating(rating) {
  return {
    ...EMPTY_FORM,
    stars: Number(rating.stars) || 0,
    comment: rating.comment || "",
    tags: Array.isArray(rating.tags) ? rating.tags.slice() : [],
    // confirmedGenuine stays false — an edit must re-confirm before resubmit.
  };
}

function scrollToTop() {
  try {
    if (typeof window.scrollTo === "function") {
      window.scrollTo(0, 0);
    }
  } catch (e) {
    // ignore
  }
}

function LoadingSkeleton() {
  return (
    <div>
      <Skeleton variant="rounded" height={88} sx={{ borderRadius: "16px" }} />
      <Skeleton variant="text" width="80%" sx={{ fontSize: "24px", mt: 9 }} />
      <Skeleton variant="text" width="60%" sx={{ fontSize: "16px" }} />
      <Skeleton
        variant="rounded"
        width={RATING_TILE_PICK * 5 + 16}
        height={RATING_TILE_PICK}
        sx={{ mt: 2, borderRadius: 0 }}
      />
    </div>
  );
}

function UnavailableNotice({ phase, onRetry }) {
  if (phase === PHASE.EXPIRED) {
    return (
      <NoticeBox title="This rating link has expired">
        Rating links stop working a couple of months after your visit. You can
        still share feedback — <RatingLink href="/me/login">log in</RatingLink>{" "}
        and{" "}
        <RatingLink href="/me/messages/care">message the care team</RatingLink>.
      </NoticeBox>
    );
  }
  if (phase === PHASE.ERROR) {
    return (
      <NoticeBox title="Something went wrong">
        <p style={{ margin: "0 0 12px" }}>
          We couldn't load your rating link. Please try again.
        </p>
        <PillButton onClick={onRetry}>Try again</PillButton>
      </NoticeBox>
    );
  }
  return (
    <NoticeBox title="This rating link isn't valid">
      Rating links are personal and only work for the visit they were sent for.
      To share feedback, <RatingLink href="/me/login">log in</RatingLink> and{" "}
      <RatingLink href="/me/messages/care">message the care team</RatingLink>.
    </NoticeBox>
  );
}

export default function PortalRateExperience() {
  const { token: paramToken } = useParams();
  // Lazy initializer: resolves (and scrubs) exactly once, on first render.
  const [token] = useState(() => resolveRatingToken(paramToken));

  const [phase, setPhase] = useState(token ? PHASE.LOADING : PHASE.INVALID);
  const [invitation, setInvitation] = useState(null);
  const [rating, setRating] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    document.title = RATING_PAGE_TITLE;
  }, []);

  useEffect(() => {
    if (!token) {
      return undefined;
    }
    let cancelled = false;
    setPhase(PHASE.LOADING);

    axios
      .get(invitationURL(token), { headers: JSON_HEADERS })
      .then(({ data }) => {
        if (cancelled) {
          return;
        }
        const status = data && data.status;
        if (status === RATING_STATUS_OK) {
          setInvitation(data);
          if (data.existingRating) {
            setRating(data.existingRating);
            setPhase(PHASE.THANKS);
          } else {
            setPhase(PHASE.RATE);
          }
        } else if (status === RATING_STATUS_EXPIRED) {
          setPhase(PHASE.EXPIRED);
        } else {
          setPhase(PHASE.INVALID);
        }
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        const status = err && err.response && err.response.status;
        setPhase(phaseForHTTPStatus(status) || PHASE.ERROR);
      });

    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  const handleFieldChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleToggleTag = useCallback((code) => {
    setForm((prev) => {
      const tags = prev.tags || [];
      return {
        ...prev,
        tags: tags.includes(code)
          ? tags.filter((c) => c !== code)
          : [...tags, code],
      };
    });
  }, []);

  const handleSubmit = () => {
    if (submitting || !canSubmitRating(form)) {
      return;
    }
    setSubmitting(true);
    setSubmitError("");

    const payload = {
      stars: form.stars,
      comment: form.comment.trim(),
      tags: form.tags || [],
      website: form.website || "",
    };

    axios
      .post(ratingURL(token), payload, { headers: JSON_HEADERS })
      .then(({ data }) => {
        setRating((data && data.rating) || payload);
        setEditing(false);
        setPhase(PHASE.THANKS);
        scrollToTop();
      })
      .catch((err) => {
        const response = (err && err.response) || {};
        const status = response.status;
        const body = response.data || {};
        const phaseChange = phaseForHTTPStatus(status);
        if (phaseChange) {
          setPhase(phaseChange);
        } else if (status === 422) {
          setSubmitError(body.error || body.message || GENERIC_SUBMIT_ERROR);
        } else if (status === 429) {
          setSubmitError(RATE_LIMITED_SUBMIT_ERROR);
        } else {
          setSubmitError(GENERIC_SUBMIT_ERROR);
        }
      })
      .then(() => setSubmitting(false));
  };

  const handleEdit = () => {
    setForm(formFromRating(rating || {}));
    setSubmitError("");
    setEditing(true);
    setPhase(PHASE.RATE);
    scrollToTop();
  };

  if (phase === PHASE.THANKS && rating) {
    return (
      <RatingShell
        background={RATING_COLORS.thanksBg}
        columnWidth={RATING_THANKS_COLUMN}
        topGap={0}
        bandOverlap={THANKS_BAND_OVERLAP}
        band={<RatingThankYouBand rating={rating} invitation={invitation} />}
      >
        <RatingThankYou
          rating={rating}
          invitation={invitation}
          onEdit={handleEdit}
        />
      </RatingShell>
    );
  }

  if (phase === PHASE.RATE && invitation) {
    return (
      <RatingShell>
        <CompanyCard />
        <RatingForm
          invitation={invitation}
          values={form}
          onChange={handleFieldChange}
          onToggleTag={handleToggleTag}
          onSubmit={handleSubmit}
          submitting={submitting}
          error={submitError}
          editing={editing}
        />
      </RatingShell>
    );
  }

  if (phase === PHASE.LOADING) {
    return (
      <RatingShell>
        <LoadingSkeleton />
      </RatingShell>
    );
  }

  return (
    <RatingShell>
      <CompanyCard />
      <div style={{ marginTop: "32px" }}>
        <UnavailableNotice
          phase={phase}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </div>
    </RatingShell>
  );
}
